/**
 * PDF estilo Amazon Energy (HTML → canvas → jsPDF).
 * Usado por Calculadora Solar e Consulta de Propostas.
 */

import jsPDF from 'jspdf'
import html2canvas from 'html2canvas'
import { buildAmazonEnergyPropostaHtml, escapeHtmlAmazon } from '../amazonEnergyPropostaHtml'
import logoAmazonEnergyPreta from '../../docs/energypreta.png'
import instalacao01 from '../../img_instalacao/Instalacao01.jpeg'
import instalacao02 from '../../img_instalacao/Instalacao02.jpeg'
import instalacao03 from '../../img_instalacao/Instalacao03.jpeg'
import instalacao04 from '../../img_instalacao/Instalacao04.jpeg'
import { HORAS_SOL, DIAS_MES, FATOR_PERDA, slugNomeArquivo } from './constantes'
import { htmlComImagensEmbutidas, nomeArquivoHtmlDePdf, salvarHtmlProposta } from './salvarHtml'

/**
 * @param {{ dados: object, userEmpresa: object }} args
 * @returns {Promise<{ fileName: string, htmlFileName: string, tipo: 'pdf' }>}
 */
export async function gerarPdfAmazon({ dados, userEmpresa }) {
  const totalComMajoracao = Number(dados.totalProposta) || 0
  const nomeClienteEsc = escapeHtmlAmazon((dados.nomeCliente || '').trim())
  const kwpStr = escapeHtmlAmazon(
    `${Number(dados.projetoKwp || 0).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 2 })} kWp`
  )
  const kwhGeracaoEst = Math.round((Number(dados.projetoKwp) || 0) * HORAS_SOL * DIAS_MES * FATOR_PERDA)
  const geracaoStr = escapeHtmlAmazon(
    `${kwhGeracaoEst.toLocaleString('pt-BR')} kWh/mês`
  )
  const investimentoStr = escapeHtmlAmazon(
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(totalComMajoracao)
  )

  const kv = dados.kitValorSelecionado
  let inversorStr
  if (dados.tipoProposta === 'personalizada' && kv && (kv.marca_inversor || kv.potencia_inversor)) {
    const partes = [kv.marca_inversor, kv.potencia_inversor ? `${kv.potencia_inversor} kW` : ''].filter(Boolean)
    inversorStr = partes.join(' ').trim()
  } else {
    inversorStr = `${dados.inversor || 0} kW`
  }
  inversorStr = escapeHtmlAmazon(inversorStr)

  const modulosStr = escapeHtmlAmazon(
    `${dados.quantidadePaineis || 0}× ${dados.moduloNome || 'Módulo'} ${dados.moduloPotencia || 0}W`
  )

  const empresaNome = escapeHtmlAmazon(userEmpresa?.nome || 'Amazon Energy')
  const slogan = escapeHtmlAmazon('Energia que Transforma')
  const logoInnerHtml = `<img src="${logoAmazonEnergyPreta}" alt="Amazon Energy" crossorigin="anonymous" />`

  const baseRef = 15600.25
  const r = Math.max(0.01, totalComMajoracao / baseRef)
  const fmtPaybackK = (milharesReais) => {
    const reais = milharesReais * 1000 * r
    const sign = reais < 0 ? '-' : '+'
    const k = Math.abs(reais) / 1000
    return `${sign}R$ ${k.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}k`
  }
  const paybackLabels = [
    escapeHtmlAmazon(fmtPaybackK(-15.6)),
    escapeHtmlAmazon(fmtPaybackK(-11.2)),
    escapeHtmlAmazon(fmtPaybackK(-6.8)),
    escapeHtmlAmazon(fmtPaybackK(-2.4)),
    escapeHtmlAmazon(fmtPaybackK(1.5))
  ]

  const localizacaoStr = escapeHtmlAmazon('Belém / Ananindeua - Pará')
  const contatoLinha = [userEmpresa?.contato, userEmpresa?.email].filter(Boolean).join(' | ')
  const contatoStr = escapeHtmlAmazon(
    contatoLinha || '(91) 9XXXX-XXXX | contato@amazonenergy.com.br'
  )

  const htmlDoc = buildAmazonEnergyPropostaHtml({
    nomeCliente: nomeClienteEsc,
    kwpStr,
    geracaoStr,
    investimentoStr,
    inversorStr,
    modulosStr,
    empresaNome,
    slogan,
    logoInnerHtml,
    localizacaoStr,
    contatoStr,
    paybackLabels,
    gallerySrc: [instalacao01, instalacao02, instalacao03, instalacao04]
  })

  const iframe = document.createElement('iframe')
  iframe.setAttribute('title', 'proposta-amazon-energy')
  iframe.style.position = 'fixed'
  iframe.style.left = '-9999px'
  iframe.style.top = '0'
  iframe.style.width = '210mm'
  iframe.style.minHeight = '297mm'
  iframe.style.border = 'none'
  document.body.appendChild(iframe)

  const doc = iframe.contentDocument || iframe.contentWindow.document
  doc.open()
  doc.write(htmlDoc)
  doc.close()

  await new Promise((resolve) => {
    if (iframe.contentDocument?.readyState === 'complete') resolve()
    else iframe.onload = () => resolve()
  })
  await new Promise((r) => setTimeout(r, 400))

  const pageEl = doc.body.querySelector('.page')
  if (!pageEl) {
    document.body.removeChild(iframe)
    throw new Error('Template da proposta não pôde ser renderizado.')
  }

  const canvas = await html2canvas(pageEl, {
    scale: 2,
    useCORS: true,
    allowTaint: false,
    logging: false,
    backgroundColor: '#ffffff'
  })
  document.body.removeChild(iframe)

  const imgData = canvas.toDataURL('image/jpeg', 0.92)
  const pdf = new jsPDF('p', 'mm', 'a4')
  const pdfW = pdf.internal.pageSize.getWidth()
  const pdfH = pdf.internal.pageSize.getHeight()
  const imgH = (canvas.height * pdfW) / canvas.width
  let offsetY = 0
  while (offsetY < imgH) {
    if (offsetY > 0) pdf.addPage()
    pdf.addImage(imgData, 'JPEG', 0, -offsetY, pdfW, imgH)
    offsetY += pdfH
  }

  const base = dados.fileNameBase
    ? dados.fileNameBase.replace(/^Proposta(_Solar)?/, 'Proposta_AmazonEnergy')
    : `Proposta_AmazonEnergy_${slugNomeArquivo(dados.nomeCliente)}_${new Date().toISOString().split('T')[0]}`
  const fileName = `${base}.pdf`
  pdf.save(fileName)

  const htmlFileName = nomeArquivoHtmlDePdf(fileName)
  const htmlPortavel = await htmlComImagensEmbutidas(htmlDoc)
  await new Promise((r) => setTimeout(r, 400))
  salvarHtmlProposta(htmlPortavel, htmlFileName)

  return { fileName, htmlFileName, tipo: 'pdf' }
}
